import React, {useState, useEffect} from 'react';
import {PixelRatio, StatusBar, StyleSheet} from 'react-native';
import {Badge, Box, Pressable, Text} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  NavigationState,
  SceneRendererProps,
  TabView,
} from 'react-native-tab-view';
import {IncomeTaxForm, PaymentForm, VatTaxForm} from './forms';
import {StackParamList} from '../../../../navigators';
import {useContextState as useUserContext} from '../../../../context/user';
import InternalTemplate from '../../../../components/templates/internal';
import {IPaymentForm, ITaxForm} from '../../../../interfaces/forms';
import {ProofKeyTab, ProofType, Role} from '../../../../constants/enumerations';
import {SpinnerModal} from '../../../../components/modals';
import {message} from '../../../../utils/messages';
import useToast from '../../../../hooks';
import {isWebApp} from '../../../../utils/platform/is-web';
import {useProofPayment} from './hooks/proof-payment.hook';
import {
  RESULT_REQUEST_CACHED_FAILED,
  ResultOfRequestCached,
} from '../../../../interfaces/types/result-of-request-cached';
import { readFile } from './utils/read-file';

type ScreenProps = NativeStackScreenProps<StackParamList, 'ProofPayment'>;

const ProofPaymentScreen = ({navigation}: ScreenProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showRemoteError, setShowRemoteError] = useState(false);

  const [index, setIndex] = React.useState(0);
  const {profile, remoteMessageError, setRemoteMessageError} = useUserContext();
  const {sendSalesmanProof, sendSubClientProof, uiConfiguration, goBackScreen} =
    useProofPayment();
  const {showProofError, showReadFileError, showRemoteMessageError} =
    useToast();

  const launchPaymentProofError = () => {
    if (remoteMessageError) {
      showRemoteMessageError(remoteMessageError);
      setRemoteMessageError(null);
    } else {
      showProofError();
    }
  };

  useEffect(() => {
    if (showRemoteError) {
      launchPaymentProofError();
      setShowRemoteError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRemoteError]);

  const getConfigurationValue = (key: string): boolean | null => {
    if (uiConfiguration) {
      const {payment, vatTax, incomeTax} = uiConfiguration.proofForm;
      if (key === ProofKeyTab.vatTax) {
        return vatTax;
      }
      if (key === ProofKeyTab.incomeTax) {
        return incomeTax;
      }
      if (key === ProofKeyTab.payment) {
        return payment;
      }
    }
    return null;
  };

  const defaultRoutes = [
    {
      key: ProofKeyTab.payment,
      title: 'payment-form-title',
    },
    {
      key: ProofKeyTab.vatTax,
      title: 'tax-form-title',
    },
    {
      key: ProofKeyTab.incomeTax,
      title: 'tax-rent-form-title',
    },
  ];

  const getRouteConfiguration = () => {
    return defaultRoutes.filter(r => getConfigurationValue(r.key));
  };

  const [routes] = React.useState(getRouteConfiguration);

  const submitPaymentProof = async (
    proof: IPaymentForm | ITaxForm,
    type: ProofType,
  ): Promise<boolean | ResultOfRequestCached> => {
    let result: boolean | ResultOfRequestCached = true;
    if (profile?.role === Role.salesman) {
      result = await submitSalesmanPaymentProof(proof, type);
    } else {
      result = await submitUserPaymentProof(proof, type);
    }

    return result;
  };

  const submitSalesmanPaymentProof = async (
    proof: IPaymentForm | ITaxForm,
    type: ProofType,
  ): Promise<ResultOfRequestCached> => {
    try {
      setIsLoading(true);

      if (await readFile(proof)) {
        const result = await sendSalesmanProof(proof, type);

        setIsLoading(false);

        return result;
      }
    } catch {
      setIsLoading(false);
      showReadFileError();
    }
    return RESULT_REQUEST_CACHED_FAILED;
  };

  const submitUserPaymentProof = async (
    proof: IPaymentForm | ITaxForm,
    type: ProofType,
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      if (await readFile(proof)) {
        return await sendSubClientProof(proof, type);
      }
    } catch {
      setIsLoading(false);
      showReadFileError();
    }
    return false;
  };

  const handleEndSubmitProof = (result: boolean | ResultOfRequestCached) => {
    setIsLoading(false);
    if (result === true) {
      gotSucess();
    } else if (result === false) {
      setShowRemoteError(true);
    } else {
      const {status, request} = result as ResultOfRequestCached;
      switch (status) {
        case 'ok':
          gotSucess();
          break;
        case 'for-cache':
          navigation.navigate('WithoutConnectionConfirm', {
            request,
            workflow: 'payment-proof',
          });
          break;
        default:
          setShowRemoteError(true);
      }
    }
  };

  const gotSucess = () => {
    navigation.navigate('ProofSuccess');
  };

  const renderScene = ({route}: any) => {
    switch (route.key) {
      case ProofKeyTab.payment:
        return (
          <PaymentForm
            onSubmit={submitPaymentProof}
            onEndSubmit={handleEndSubmitProof}
          />
        );

      case ProofKeyTab.vatTax:
        return (
          <VatTaxForm
            onSubmit={submitPaymentProof}
            onEndSubmit={handleEndSubmitProof}
          />
        );

      case ProofKeyTab.incomeTax:
        return (
          <IncomeTaxForm
            onSubmit={submitPaymentProof}
            onEndSubmit={handleEndSubmitProof}
          />
        );
    }
  };

  const getTabBackColor = (position: number) => {
    return index === position ? '#0088FF' : 'gray.100';
  };

  const getTabForeColor = (position: number, key: string) => {
    const configuration = uiConfiguration?.proofForm;
    const color =
      configuration && getConfigurationValue(key) ? '#0088FF' : 'gray.300';

    return index === position ? 'white' : color;
  };

  const handleTabPress = (position: number, key: string) => {
    if (getConfigurationValue(key)) {
      setIndex(position);
    }
  };

  const renderTabBar = (
    _props: SceneRendererProps & {
      navigationState: NavigationState<{
        key: string;
        title: string;
      }>;
    },
  ) => {
    return (
      <>
        <Box flexDirection="row">
          {defaultRoutes.map((route: any, position: number) => {
            const backgroundColor = getTabBackColor(position);
            const color = getTabForeColor(position, route.key);

            return (
              <Box
                key={`tab-${position.toString()}`}
                flex={1}
                alignItems="center"
                paddingBottom={42 / PixelRatio.get()}>
                <Pressable onPress={() => handleTabPress(position, route.key)}>
                  <Badge
                    paddingLeft="8"
                    paddingRight="8"
                    paddingTop="2"
                    paddingBottom="2"
                    backgroundColor={backgroundColor}
                    borderRadius="20">
                    <Text color={color} fontWeight="semibold">
                      {message(route.title).toUpperCase()}
                    </Text>
                  </Badge>
                </Pressable>
              </Box>
            );
          })}
        </Box>
      </>
    );
  };

  const handleGoBack = () => {
    navigation.navigate(goBackScreen);
  };
  return (
    <InternalTemplate
      title={'proof-payment-load-vouchers'}
      childContainerStyle={isWebApp ? {} : styles.childContainerStyle}
      backButton={{
        label: 'back-dashboard',
        onPress: () => handleGoBack(),
        usage: 'external',
      }}>
      <SpinnerModal
        show={isLoading}
        title={message('send-proof-loading-title')}
      />
      <TabView
        navigationState={{
          index,
          routes,
        }}
        renderScene={renderScene}
        renderTabBar={renderTabBar}
        onIndexChange={setIndex}
        style={
          isWebApp
            ? styles.tabView
            : {
                marginTop: StatusBar.currentHeight,
              }
        }
      />
    </InternalTemplate>
  );
};

const styles = StyleSheet.create({
  childContainerStyle: {
    height: '100%',
  },
  tabView: {
    marginTop: StatusBar.currentHeight,
  },
});

export default ProofPaymentScreen;
